import { zodResolver } from "@hookform/resolvers/zod";
import { changePasswordSchema, type Locale, passwordSchema } from "@shelflist/shared";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Field, fieldA11y } from "@/components/field";
import { FormError } from "@/components/form-error";
import { Page } from "@/components/page";
import { ColorThemeCard } from "@/components/settings/color-theme-card";
import { SuggestionLanguagesCard } from "@/components/settings/suggestion-languages-card";
import { SuggestionScopeCard } from "@/components/settings/suggestion-scope-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from "@/i18n/languages";
import { api, call } from "@/lib/api";
import { applyFieldErrors, useErrorMessage } from "@/lib/errors";
import type { Me } from "@/lib/queries";
import { useLogout, useMe, useSetLanguage } from "@/lib/session";

export const Route = createFileRoute("/_app/settings/account")({
  component: AccountPage,
});

function AccountPage() {
  const { t } = useTranslation(["settings", "common"]);
  const me = useMe();
  const logout = useLogout();

  return (
    <Page title={t("title")} width="narrow">
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("profile.title")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2">
            <dt className="text-muted-foreground">{t("common:fields.name")}</dt>
            <dd>{me.name}</dd>
            <dt className="text-muted-foreground">{t("common:fields.email")}</dt>
            <dd className="break-all">{me.email}</dd>
            <dt className="text-muted-foreground">{t("common:fields.role")}</dt>
            <dd>{t(`common:roles.${me.role}`)}</dd>
          </dl>
        </CardContent>
      </Card>
      <ColorThemeCard me={me} />
      <LanguageCard me={me} />
      <SuggestionLanguagesCard />
      <SuggestionScopeCard />
      <PasswordCard />
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("session.title")}</h2>
          </CardTitle>
          <CardDescription>{t("session.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => void logout()}>
            <LogOut aria-hidden />
            {t("common:nav.logout")}
          </Button>
        </CardContent>
      </Card>
    </Page>
  );
}

const BROWSER_DEFAULT = "browser";

function LanguageCard({ me }: { me: Me }) {
  const { t } = useTranslation(["settings", "common"]);
  const update = useSetLanguage();

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{t("language.title")}</h2>
        </CardTitle>
        <CardDescription>{t("language.description")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        <Label htmlFor="language">{t("common:language.label")}</Label>
        <Select
          value={me.locale ?? BROWSER_DEFAULT}
          disabled={update.isPending}
          onValueChange={(value) =>
            update.mutate(value === BROWSER_DEFAULT ? null : (value as Locale))
          }
        >
          <SelectTrigger id="language" className="w-full sm:w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={BROWSER_DEFAULT}>{t("common:language.browserDefault")}</SelectItem>
            {SUPPORTED_LANGUAGES.map((language) => (
              <SelectItem key={language} value={language} lang={language}>
                {LANGUAGE_NAMES[language]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );
}

const passwordFormSchema = changePasswordSchema
  .extend({ newPassword: passwordSchema, confirmPassword: z.string() })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ["confirmPassword"],
    error: "passwords_mismatch",
  });
type PasswordFormValues = z.input<typeof passwordFormSchema>;

function PasswordCard() {
  const { t } = useTranslation(["settings", "common"]);
  const errorMessage = useErrorMessage();
  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });
  const change = useMutation({
    mutationFn: ({ currentPassword, newPassword }: PasswordFormValues) =>
      call(api.me.password.$post({ json: { currentPassword, newPassword } })),
    meta: { handlesErrors: true },
    onSuccess: () => {
      form.reset();
      toast.success(t("password.success"));
    },
    onError: (error) => {
      applyFieldErrors(error, form.setError, ["currentPassword", "newPassword"]);
    },
  });
  const { errors } = form.formState;
  const hasFieldErrors = Object.keys(errors).length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{t("password.title")}</h2>
        </CardTitle>
        <CardDescription>{t("password.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          noValidate
          className="grid gap-4"
          onSubmit={form.handleSubmit((values) => change.mutate(values))}
        >
          <FormError
            message={change.error && !hasFieldErrors ? errorMessage(change.error) : null}
          />
          <Field
            id="currentPassword"
            label={t("password.current")}
            error={errors.currentPassword?.message}
          >
            <Input
              {...fieldA11y("currentPassword", errors.currentPassword?.message)}
              type="password"
              autoComplete="current-password"
              {...form.register("currentPassword")}
            />
          </Field>
          <Field
            id="newPassword"
            label={t("common:fields.newPassword")}
            error={errors.newPassword?.message}
            hint={t("common:passwordHint")}
          >
            <Input
              {...fieldA11y("newPassword", errors.newPassword?.message)}
              type="password"
              autoComplete="new-password"
              {...form.register("newPassword")}
            />
          </Field>
          <Field
            id="confirmPassword"
            label={t("common:fields.confirmPassword")}
            error={errors.confirmPassword?.message}
          >
            <Input
              {...fieldA11y("confirmPassword", errors.confirmPassword?.message)}
              type="password"
              autoComplete="new-password"
              {...form.register("confirmPassword")}
            />
          </Field>
          <div>
            <Button type="submit" disabled={change.isPending}>
              {t("password.submit")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
